import { useMemo, useState } from 'react';
import { parties, workplace } from '../api/endpoints';
import type { AttentionBoard, AttentionRow, ClientAccount } from '../api/types';
import { Card, Column, DataTable, ErrorBox, Field, fmtDate, label, PageHeader } from '../components/ui';
import { navigate, useAsync } from '../hooks';

const DOCUMENT_TYPES: { value: string; title: string }[] = [
  { value: 'ss', title: 'СС' },
  { value: 'ds', title: 'ДС' },
  { value: 'refusal_letter', title: 'Отказное письмо' },
  { value: 'aip', title: 'АИП' },
  { value: 'sgr', title: 'СГР' },
  { value: 'other', title: 'Другое' },
  { value: 'unknown', title: 'Не определено' },
];

function fmtOverdue(seconds?: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days} дн. ${hours} ч`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
}

function responsibleName(row: AttentionRow): string {
  return row.assignee?.full_name ?? row.assignee?.username
    ?? row.owner?.full_name ?? row.owner?.username ?? '—';
}

const COLUMNS: Column<AttentionRow>[] = [
  {
    key: 'request_number', title: 'Заявка',
    render: (r) => <a href={`#/requests/${r.request_id}`}>{r.request_number ?? r.request_id.slice(0, 8)}</a>,
  },
  { key: 'client_name', title: 'Клиент', render: (r) => r.client_name ?? '—' },
  { key: 'responsible', title: 'Ответственный', render: responsibleName },
  {
    key: 'description', title: 'Описание',
    render: (r) => (
      <span>
        <span className="muted">{r.kind_label ?? label(r.kind)}</span>
        {r.description && r.description !== r.kind_label ? ` — ${r.description}` : ''}
      </span>
    ),
  },
  { key: 'due_at', title: 'Срок', render: (r) => fmtDate(r.due_at) },
  {
    key: 'overdue_seconds', title: 'Просрочка',
    render: (r) => (r.overdue_seconds != null && r.overdue_seconds > 0
      ? <span className="badge bad">{fmtOverdue(r.overdue_seconds)}</span>
      : '—'),
  },
  {
    key: 'action', title: 'Действие',
    render: (r) => (r.action_url
      ? <a href={r.action_url} onClick={(e) => e.stopPropagation()}>Открыть</a>
      : '—'),
  },
];

export function AttentionPage() {
  const [assignee, setAssignee] = useState('');
  const [clientId, setClientId] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');

  const params = useMemo<Record<string, unknown>>(() => ({
    assignee: assignee || undefined,
    client: clientId || undefined,
    document_type: documentType || undefined,
    due_from: dueFrom || undefined,
    due_to: dueTo || undefined,
  }), [assignee, clientId, documentType, dueFrom, dueTo]);

  const board = useAsync<AttentionBoard>(() => workplace.attention(params), [params]);
  const clients = useAsync<ClientAccount[]>(
    () => parties.clientAccounts().catch(() => [] as ClientAccount[]), []);

  // Варианты фильтра «Ответственный» — из уже загруженных строк доски.
  const assigneeOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const cat of board.data?.categories ?? []) {
      for (const row of cat.rows) {
        const id = row.responsible_id ?? row.assignee?.id ?? row.owner?.id;
        const name = responsibleName(row);
        if (id != null && name !== '—') map.set(String(id), name);
      }
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [board.data]);

  return (
    <div>
      <PageHeader title="Требует внимания">
        <button className="btn" onClick={board.reload}>Обновить</button>
      </PageHeader>

      <Card>
        <div className="form-row">
          <Field label="Ответственный">
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Все</option>
              {assigneeOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Клиент">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Все</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.party_name ?? c.party}</option>
              ))}
            </select>
          </Field>
          <Field label="Вид документа">
            <select value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
              <option value="">Все</option>
              {DOCUMENT_TYPES.map((d) => <option key={d.value} value={d.value}>{d.title}</option>)}
            </select>
          </Field>
          <Field label="Срок с">
            <input type="date" value={dueFrom} onChange={(e) => setDueFrom(e.target.value)} />
          </Field>
          <Field label="Срок по">
            <input type="date" value={dueTo} onChange={(e) => setDueTo(e.target.value)} />
          </Field>
        </div>
      </Card>

      <ErrorBox error={board.error} onRetry={board.reload} />

      {(board.data?.categories ?? []).map((cat) => (
        <Card key={cat.key} title={`${cat.title} (${cat.rows.length})`}>
          <DataTable columns={COLUMNS} rows={cat.rows}
                     onRowClick={(r) => navigate(`/requests/${r.request_id}`)}
                     empty="Ничего нет" />
        </Card>
      ))}
      {board.data && (board.data.categories ?? []).length === 0 && (
        <Card><div className="empty">Нет данных</div></Card>
      )}
    </div>
  );
}
